<template>
  <header class="app-header">
    <div class="header-inner">
      <!-- Vänster: Logotyp och live-indikator -->
      <div class="brand-section">
        <div 
          class="logo-row" 
          @click="$emit('refresh')"
          role="button"
          tabindex="0"
          :title="t('refreshFeed')"
          aria-label="Woke News — Scroll to top and refresh"
        >
          <img src="/favicon.png" alt="Woke News Capybara" class="capybara-icon" />
          <h1 class="logo">WOKE NEWS</h1>
        </div>
        <div class="date-tagline">
          <span class="current-date">{{ currentDateFormatted }}</span>
          <span class="separator">•</span>
          <span class="tagline">{{ t('tagline') }}</span>
        </div>
      </div>

      <!-- Höger: Verktyg (Sök, Språklista, Kompakt-vy, Uppdatera, Tema) -->
      <div class="actions-section">
        <!-- Sökfält -->
        <div class="search-box" :class="{ 'has-query': searchQuery.length > 0 }">
          <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input 
            type="text" 
            :value="searchQuery" 
            @input="$emit('update:searchQuery', $event.target.value)"
            :placeholder="t('searchPlaceholder')"
            :aria-label="t('searchPlaceholder')"
          >
          <button 
            v-if="searchQuery" 
            type="button" 
            class="clear-btn" 
            @click="$emit('update:searchQuery', '')"
            :aria-label="t('clearSearch')"
          >
            ×
          </button>
        </div>

        <!-- Språklista (Dropdown: EN, DE, SV) -->
        <div class="lang-dropdown-wrapper" ref="langDropdown">
          <button 
            type="button" 
            class="lang-btn" 
            @click="toggleLangDropdown"
            :aria-expanded="isLangOpen"
            :title="t('language')"
            aria-label="Select language"
          >
            <span class="lang-flag">{{ currentLangMeta.flag }}</span>
            <span class="lang-code">{{ currentLangMeta.code.toUpperCase() }}</span>
            <svg class="dropdown-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          <!-- Dropdown-meny -->
          <div v-if="isLangOpen" class="lang-menu">
            <button
              v-for="l in languages"
              :key="l.code"
              type="button"
              class="lang-menu-item"
              :class="{ active: lang === l.code }"
              @click="selectLang(l.code)"
            >
              <span class="lang-item-flag">{{ l.flag }}</span>
              <span class="lang-item-label">{{ l.label }}</span>
              <span v-if="lang === l.code" class="check-icon">✓</span>
            </button>
          </div>
        </div>

        <!-- Kompakt-läge knapp -->
        <button 
          type="button" 
          class="icon-btn" 
          :class="{ active: isCompact }"
          @click="$emit('toggle-compact')"
          :title="isCompact ? t('toggleStandard') : t('toggleCompact')"
          :aria-label="t('toggleCompact')"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>

        <!-- Tema-knapp (Ljus / Mörk) -->
        <button 
          type="button" 
          class="icon-btn theme-btn" 
          @click="$emit('toggle-theme')"
          :title="isDark ? t('toggleThemeLight') : t('toggleThemeDark')"
          :aria-label="isDark ? t('toggleThemeLight') : t('toggleThemeDark')"
        >
          <!-- Sol ikon -->
          <svg v-if="isDark" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="5"></circle>
            <line x1="12" y1="1" x2="12" y2="3"></line>
            <line x1="12" y1="21" x2="12" y2="23"></line>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
            <line x1="1" y1="12" x2="3" y2="12"></line>
            <line x1="21" y1="12" x2="23" y2="12"></line>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
          </svg>
          <!-- Måne ikon -->
          <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
          </svg>
        </button>
      </div>
    </div>
  </header>
</template>

<script>
import { t, AVAILABLE_LANGUAGES } from '../services/i18n'

export default {
  name: 'NewsHeader',
  props: {
    lang: {
      type: String,
      default: 'en'
    },
    searchQuery: {
      type: String,
      default: ''
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isDark: {
      type: Boolean,
      default: false
    },
    isCompact: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:searchQuery', 'refresh', 'toggle-theme', 'toggle-compact', 'change-lang'],
  data() {
    return {
      isLangOpen: false,
      languages: AVAILABLE_LANGUAGES
    }
  },
  computed: {
    currentLangMeta() {
      return this.languages.find(l => l.code === this.lang) || this.languages[0]
    },
    currentDateFormatted() {
      const now = new Date()
      let locale = 'en-US'
      if (this.lang === 'sv') locale = 'sv-SE'
      else if (this.lang === 'de') locale = 'de-DE'

      const formatted = now.toLocaleDateString(locale, {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      })
      return formatted.charAt(0).toUpperCase() + formatted.slice(1)
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    t(key) {
      return t(this.lang, key)
    },
    toggleLangDropdown(e) {
      e.stopPropagation()
      this.isLangOpen = !this.isLangOpen
    },
    selectLang(code) {
      this.$emit('change-lang', code)
      this.isLangOpen = false
    },
    handleOutsideClick(e) {
      if (this.$refs.langDropdown && !this.$refs.langDropdown.contains(e.target)) {
        this.isLangOpen = false
      }
    }
  }
}
</script>

<style scoped>
.app-header {
  background-color: transparent;
  border-bottom: 1px solid var(--border-color);
}

.header-inner {
  max-width: 900px;
  margin: 0 auto;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.brand-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.logo-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.logo-row:hover .logo {
  color: var(--accent-hover);
}

.capybara-icon {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.logo {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1;
}

.date-tagline {
  font-size: 0.76rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.separator {
  opacity: 0.5;
}

.actions-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
  background-color: var(--input-bg);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 0 10px;
  width: 170px;
  height: 34px;
  transition: all 0.2s ease;
}

.search-box:focus-within {
  width: 210px;
  border-color: var(--accent-color);
}

.search-icon {
  color: var(--text-muted);
  flex-shrink: 0;
  margin-right: 6px;
}

.search-box input {
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.83rem;
  color: var(--text-primary);
  font-family: inherit;
}

.search-box input::placeholder {
  color: var(--text-muted);
  opacity: 0.8;
}

.clear-btn {
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
}

/* Språkval dropdown */
.lang-dropdown-wrapper {
  position: relative;
}

.lang-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background-color: var(--btn-bg);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  padding: 0 10px;
  height: 36px;
  border-radius: 18px;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.lang-btn:hover {
  background-color: var(--btn-hover-bg);
  border-color: var(--accent-color);
}

.lang-flag {
  font-size: 0.95rem;
}

.dropdown-arrow {
  color: var(--text-muted);
  transition: transform 0.15s ease;
}

.lang-menu {
  position: absolute;
  top: 40px;
  right: 0;
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 4px;
  min-width: 130px;
  z-index: 200;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lang-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 10px;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.82rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.12s ease;
  -webkit-tap-highlight-color: transparent;
}

.lang-menu-item:hover {
  background-color: var(--item-hover-bg);
}

.lang-menu-item.active {
  background-color: var(--pill-bg);
  color: var(--accent-color);
}

.check-icon {
  margin-left: auto;
  color: #10b981;
  font-size: 0.85rem;
  font-weight: bold;
}

.icon-btn {
  background-color: var(--btn-bg);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.icon-btn:hover {
  background-color: var(--btn-hover-bg);
}

.icon-btn.active {
  background-color: var(--accent-color);
  color: #ffffff;
  border-color: var(--accent-color);
}

.spinning svg {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% { transform: rotate(360deg); }
}

@media (max-width: 650px) {
  .header-inner {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  .brand-section {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  .tagline {
    display: none;
  }
  .separator {
    display: none;
  }
  .actions-section {
    justify-content: space-between;
  }
  .search-box {
    flex: 1;
    width: auto;
  }
  .search-box:focus-within {
    width: auto;
  }
}
</style>
